import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import styles from './Motion.module.css';
import { COLLAPSE_MS } from './motion-timing';

interface SmoothCollapseProps {
  readonly open: boolean;
  readonly children: ReactNode;
  readonly className?: string;
  /** Overrides the open/close duration, for a collapse that should read as
   *  a deliberate motion rather than a quick toggle. */
  readonly durationMs?: number;
}

/** Animates its children's height open and closed. Content mounts the moment
 *  `open` turns true (so it is in the DOM synchronously, for focus and tests)
 *  and unmounts once the close animation has run. */
export function SmoothCollapse({
  open,
  children,
  className,
  durationMs = COLLAPSE_MS,
}: SmoothCollapseProps) {
  const [mounted, setMounted] = useState(open);
  const [expanded, setExpanded] = useState(open);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    setExpanded(false);
    const timer = setTimeout(() => setMounted(false), durationMs);
    return () => clearTimeout(timer);
  }, [open, durationMs]);

  // Opening is two steps. The panel first commits closed (0fr); reading its
  // geometry forces the browser to lay out that closed state, and only on
  // the next frame does it switch to open. Without the forced layout a panel
  // that has just mounted has no computed starting height, so the browser
  // jumps straight to open instead of animating.
  useLayoutEffect(() => {
    if (!open || !mounted || expanded) return;
    rootRef.current?.getBoundingClientRect();
    const frame = requestAnimationFrame(() => setExpanded(true));
    return () => cancelAnimationFrame(frame);
  }, [open, mounted, expanded]);

  if (!mounted) {
    return null;
  }

  const classNames = className
    ? `${styles.collapse} ${className}`
    : styles.collapse;

  return (
    <div
      ref={rootRef}
      className={classNames}
      data-open={expanded}
      style={
        durationMs === COLLAPSE_MS
          ? undefined
          : ({ '--moo-duration': `${durationMs}ms` } as CSSProperties)
      }
    >
      <div className={styles.collapseInner}>{children}</div>
    </div>
  );
}
